import type {
  AccountUpdateInput,
  Configuration,
  ConnectAwsOrganizationRequest,
  CreateAccountInput,
  Condition,
  UpdateRestoreAccountConnectivityConfigRequest,
  ListSourceAccountsRequest,
  ConnectGcpOrganizationRequest,
  ConnectGcpFolderRequest,
  ConnectAzureBackupApplicationsRequest,
} from '@repo/api-gw-sdk';
import {
  AccountsApi,
  CreateProjectRestoreAccountApi,
  CreateProjectSourceAccountApi,
  GetAWSStackDetailsApi,
  ListProjectRestoreAccountsApi,
  ListProjectSourceAccountsApi,
  ListScanningAccountsApi,
  ListVaultAccountsApi,
  UpdateProjectRestoreAccountApi,
  GetProjectSourceAccountConfigurationApi,
  GetRestoreCloudAccountTagsConfigurationApi,
  UpdateRestoreCloudAccountTagsConfigurationApi,
  DiscoveryStatusApi,
  UpdateRestoreAccountNameApi,
  UpdateSourceAccountNameApi,
  ConnectAzureSourceAccountApi,
  ConnectAzureRestoreAccountApi,
  ConnectAzureBackupApplicationsApi,
  ConnectAzureVaultAccountApi,
  GetAzureBackupApplicationsApi,
  ConnectAzureBackupAccountApi,
  ConnectBYOAzureBackupApplicationsApi,
  Provider,
} from '@repo/api-gw-sdk';

import { toJsonText } from '@/components/queryBuilder/QueryLanguageConverter';
import { encodePageToken } from '@/utils/string';

import type { HttpClient } from './httpClient';

const baseUrl = '/accounts';

export const cloudAccounts = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const listSourceAccountsApi = new ListProjectSourceAccountsApi(config);
  const createSourceAccountApi = new CreateProjectSourceAccountApi(config);
  const listVaultAccountsApi = new ListVaultAccountsApi(config);
  const listRestoreAccountsApi = new ListProjectRestoreAccountsApi(config);
  const createRestoreAccountApi = new CreateProjectRestoreAccountApi(config);
  const updateRestoreAccountApi = new UpdateProjectRestoreAccountApi(config);
  const connectAzureSourceApi = new ConnectAzureSourceAccountApi(config);
  const connectAzureRestoreApi = new ConnectAzureRestoreAccountApi(config);
  const connectBYOAzureBackupApplicationsApi =
    new ConnectBYOAzureBackupApplicationsApi(config);
  const connectAzureBackupApplicationsApi =
    new ConnectAzureBackupApplicationsApi(config);
  const connectAzureVaultApi = new ConnectAzureVaultAccountApi(config);
  const connectAzureBackupAccountApi = new ConnectAzureBackupAccountApi(config);
  const getAzureBackupApplicationsApi = new GetAzureBackupApplicationsApi(
    config
  );

  const listScanningAccountsApi = new ListScanningAccountsApi(config);
  const accountsApi = new AccountsApi(config);
  const discoveryStatusApi = new DiscoveryStatusApi(config);

  const getAWSStackDetailsApi = new GetAWSStackDetailsApi(config);
  const getProjectSourceAccountConfigurationApi =
    new GetProjectSourceAccountConfigurationApi(config);

  const getRestoreAccountTagsApi =
    new GetRestoreCloudAccountTagsConfigurationApi(config);
  const setRestoreAccountTagsApi =
    new UpdateRestoreCloudAccountTagsConfigurationApi(config);
  const updateRestoreAccountNameApi = new UpdateRestoreAccountNameApi(config);
  const updateSourceAccountNameApi = new UpdateSourceAccountNameApi(config);

  return {
    source: {
      useListV2: (
        pageIndex?: number,
        pageSize?: number,
        request?: ListSourceAccountsRequest
      ) =>
        httpClient.execute(
          (...args: Parameters<typeof accountsApi.listSourceAccounts>) =>
            accountsApi.listSourceAccounts(
              args[0],
              typeof pageIndex === 'number'
                ? encodePageToken(pageIndex)
                : undefined,
              args[2],
              args[3]
            ),
          baseUrl,
          '/source-accounts-v2',
          currentProjectId,
          `${pageIndex}`,
          pageSize,
          request || {}
        ),
      syncPermissions: (accountId: string) =>
        accountsApi.syncSourceAccountPermissions(currentProjectId, accountId),
      useDiscoveryStatus: (accountId: string) =>
        httpClient.execute(
          discoveryStatusApi.discoveryStatus.bind(discoveryStatusApi),
          baseUrl,
          '/source-account-discovery-status',
          currentProjectId,
          accountId
        ),
      create: (account: CreateAccountInput) =>
        createSourceAccountApi.createProjectSourceAccount(
          currentProjectId,
          account
        ),
      connectAzure: (data: {
        tenantId: string;
        sourceSubscriptionId: string;
        sourceManagementGroupId: string;
        eonInternalResourceGroupName?: string;
        name?: string;
      }) => {
        return connectAzureSourceApi.connectAzureSourceAccount(
          currentProjectId,
          {
            tenantId: data.tenantId,
            subscriptionId: data.sourceSubscriptionId,
            managementGroupId: data.sourceManagementGroupId,
            eonInternalResourceGroupName: data.eonInternalResourceGroupName,
            name: data.name,
          }
        );
      },
      disconnect: (id: string) =>
        accountsApi.disconnectSourceAccount(currentProjectId, id),
      reconnect: (id: string) =>
        accountsApi.reconnectSourceAccount(currentProjectId, id),
      useList: (
        projectId?: string,
        pageIndex?: number,
        pageSize?: number,
        sorting?: string,
        filter?: Condition
      ) =>
        httpClient.execute(
          (
            ...args: Parameters<
              typeof listSourceAccountsApi.listProjectSourceAccounts
            >
          ) =>
            !projectId && !currentProjectId
              ? Promise.resolve(undefined)
              : listSourceAccountsApi.listProjectSourceAccounts(...args),
          baseUrl,
          '/source-accounts',
          projectId || currentProjectId,
          pageIndex,
          pageSize,
          sorting,
          filter ? toJsonText(filter) : undefined
        ),
      config: {
        useConfig: (accountId: string) =>
          httpClient.execute(
            (
              ...args: Parameters<
                typeof getProjectSourceAccountConfigurationApi.getProjectSourceAccountConfiguration
              >
            ) =>
              accountId
                ? getProjectSourceAccountConfigurationApi.getProjectSourceAccountConfiguration(
                    ...args
                  )
                : Promise.resolve(undefined),
            baseUrl,
            '/source-account-config',
            accountId,
            currentProjectId
          ),
      },
      metrics: {
        useMetric: (accountId: string) => {
          return httpClient.execute(
            accountsApi.getSourceAccountMetricsConfig.bind(accountsApi),
            baseUrl,
            '/restore-account-job-metrics',
            accountId,
            currentProjectId
          );
        },
        enable: (accountId: string, region: string) => {
          return accountsApi.enableSourceAccountMetricsConfig(
            accountId,
            currentProjectId,
            {
              aws: {
                region: region,
              },
            }
          );
        },
        disable: (accountId: string) => {
          return accountsApi.disableSourceAccountMetricsConfig(
            accountId,
            currentProjectId
          );
        },
      },
      name: {
        update: (accountId: string, name?: string) =>
          updateSourceAccountNameApi.updateSourceAccountName(
            accountId,
            currentProjectId,
            name
          ),
      },
    },
    vault: {
      create: (account: CreateAccountInput) =>
        accountsApi
          .connectCustomerVaultAccount({
            vaultAccountAttributes: {
              cloudProvider: Provider.Aws,
              aws: { roleArn: account.role },
            },
          })
          .then((res) => ({ id: res.vaultAccount.id })),
      connectAzure: (data: {
        tenantId: string;
        vaultSubscriptionId: string;
      }) => {
        return connectAzureVaultApi.connectAzureVaultAccount(currentProjectId, {
          tenantId: data.tenantId,
          subscriptionId: data.vaultSubscriptionId,
        });
      },
      useList: () =>
        httpClient.execute(
          listVaultAccountsApi.listVaultAccounts.bind(listVaultAccountsApi),
          baseUrl,
          '/vault-accounts'
        ),
      disconnect: (accountId: string) =>
        accountsApi.disconnectCustomerVaultAccount(accountId),
      reconnect: (accountId: string) =>
        accountsApi.reconnectCustomerVaultAccount(accountId),
    },
    restore: {
      syncPermissions: (accountId: string) =>
        accountsApi.syncRestoreAccountPermissions(currentProjectId, accountId),
      create: (account: CreateAccountInput) =>
        createRestoreAccountApi.createProjectRestoreAccount(
          currentProjectId,
          account
        ),
      connectAzure: (data: {
        tenantId: string;
        restoreSubscriptionId: string;
        restoreResourceGroupName?: string;
        location?: string;
        eonInternalResourceGroupName?: string;
        name?: string;
      }) => {
        return connectAzureRestoreApi.connectAzureRestoreAccount(
          currentProjectId,
          {
            tenantId: data.tenantId,
            subscriptionId: data.restoreSubscriptionId,
            resourceGroupName: data.restoreResourceGroupName,
            location: data.location,
            eonInternalResourceGroupName: data.eonInternalResourceGroupName,
            name: data.name,
          }
        );
      },
      update: (accountId: string, account: AccountUpdateInput) =>
        updateRestoreAccountApi.updateProjectRestoreAccount(
          accountId,
          currentProjectId,
          account
        ),
      disconnect: (id: string) =>
        accountsApi.disconnectRestoreAccount(currentProjectId, id),
      reconnect: (id: string) =>
        accountsApi.reconnectRestoreAccount(currentProjectId, id),
      useList: (
        pageIndex?: number,
        pageSize?: number,
        sorting?: string,
        filter?: Condition
      ) =>
        httpClient.execute(
          listRestoreAccountsApi.listProjectRestoreAccounts.bind(
            listRestoreAccountsApi
          ),
          baseUrl,
          '/restore-accounts',
          currentProjectId,
          pageIndex,
          pageSize,
          sorting,
          filter ? toJsonText(filter) : undefined
        ),
      connectivity: {
        useConnectivity: (accountId: string) =>
          httpClient.execute(
            accountsApi.getRestoreAccountConnectivityConfig.bind(accountsApi),
            baseUrl,
            '/restore-account-connectivity-config',
            accountId,
            currentProjectId
          ),
        update: (
          accountId: string,
          cloudAccountConfiguration: UpdateRestoreAccountConnectivityConfigRequest
        ) =>
          accountsApi.updateRestoreAccountConnectivityConfig(
            accountId,
            currentProjectId,
            cloudAccountConfiguration
          ),
      },
      tags: {
        useTags: (accountId: string) =>
          httpClient.execute(
            getRestoreAccountTagsApi.getRestoreCloudAccountTagsConfiguration.bind(
              getRestoreAccountTagsApi
            ),
            baseUrl,
            '/restore-account-tags',
            accountId,
            currentProjectId
          ),
        set: (accountId: string, tags: Record<string, string>) =>
          setRestoreAccountTagsApi.updateRestoreCloudAccountTagsConfiguration(
            accountId,
            currentProjectId,
            Object.entries(tags).map(([key, value]) => ({
              key,
              value,
            }))
          ),
      },
      metrics: {
        useMetric: (accountId: string) => {
          return httpClient.execute(
            accountsApi.getRestoreAccountMetricsConfig.bind(accountsApi),
            baseUrl,
            '/restore-account-job-metrics',
            accountId,
            currentProjectId
          );
        },
        enable: (accountId: string, region: string) => {
          return accountsApi.enableRestoreAccountMetricsConfig(
            accountId,
            currentProjectId,
            {
              aws: {
                region: region,
              },
            }
          );
        },
        disable: (accountId: string) => {
          return accountsApi.disableRestoreAccountMetricsConfig(
            accountId,
            currentProjectId
          );
        },
      },
      name: {
        update: (accountId: string, name?: string) =>
          updateRestoreAccountNameApi.updateRestoreAccountName(
            accountId,
            currentProjectId,
            name
          ),
      },
    },
    scanning: {
      create: () => {
        throw new Error('Not implemented');
      },
      useList: () =>
        httpClient.execute(
          listScanningAccountsApi.listScanningAccounts.bind(
            listScanningAccountsApi
          ),
          baseUrl,
          '/scanning-accounts'
        ),
      useGetGcsServiceAgent: (accountId: string) =>
        httpClient.execute(
          accountsApi.getGcsServiceAgent.bind(accountsApi),
          baseUrl,
          'gcs-service-agent',
          accountId
        ),
      connectAzure: (data: {
        tenantId: string;
        backupSubscriptionId: string;
        regionName: string;
        adxClusterId: string;
        adxClusterIdentity: string;
        adxClusterUri: string;
        backupIdentityResourceId?: string;
      }) => {
        return connectAzureBackupAccountApi.connectAzureBackupAccount(
          currentProjectId,
          {
            tenantId: data.tenantId,
            subscriptionId: data.backupSubscriptionId,
            location: data.regionName,
            adxClusterId: data.adxClusterId,
            adxIdentity: data.adxClusterIdentity,
            adxClusterUri: data.adxClusterUri,
            federatedManagedIdentityResourceId: data.backupIdentityResourceId,
          }
        );
      },
    },
    azureApps: {
      connect: ({
        eonBackupServicePrincipalId,
        customerBackupApplicationId,
        customerBackupServicePrincipalId,
        customerTenantId,
        federatedManagedIdentityResourceId,
      }: ConnectAzureBackupApplicationsRequest) => {
        return connectAzureBackupApplicationsApi.connectAzureBackupApplications(
          currentProjectId,
          {
            eonBackupServicePrincipalId, // backup_app_service_principal_id
            customerBackupApplicationId, // restore_app_application_id
            customerBackupServicePrincipalId, // restore_app_service_principal_id
            customerTenantId,
            federatedManagedIdentityResourceId, // managed_identity_resource_id
          }
        );
      },
      connectBYO: (data: {
        customerTenantId: string;
        keyVaultAccessServicePrincipalId: string;
        managementAppId: string;
        managementServicePrincipalId: string;
        backupAppId: string;
        backupServicePrincipalId: string;
        secretUrl: string;
      }) => {
        return connectBYOAzureBackupApplicationsApi.connectBYOAzureBackupApplications(
          currentProjectId,
          {
            keyVaultAccessServicePrincipalId:
              data.keyVaultAccessServicePrincipalId,
            managementAppId: data.managementAppId,
            managementServicePrincipalId: data.managementServicePrincipalId,
            backupAppId: data.backupAppId,
            backupServicePrincipalId: data.backupServicePrincipalId,
            secretUrl: data.secretUrl,
            customerTenantId: data.customerTenantId,
          }
        );
      },
      useListTenants: () => {
        return httpClient.execute(
          accountsApi.getAzureOnboardedTenants.bind(accountsApi),
          baseUrl,
          '/azure/list-tenants',
          currentProjectId
        );
      },
      useApps: (
        accountType?: 'Source' | 'Scanning' | 'Vault' | 'Restore',
        tenantId?: string
      ) => {
        return httpClient.execute(
          // https://linear.app/eon-io/issue/EON-4754/create-different-getapplications-api-for-restore-type
          (accountType === 'Restore' || accountType === 'Source') && !tenantId
            ? (
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                ...args: Parameters<
                  typeof getAzureBackupApplicationsApi.getAzureBackupApplications
                >
              ) => Promise.resolve(undefined)
            : getAzureBackupApplicationsApi.getAzureBackupApplications.bind(
                getAzureBackupApplicationsApi
              ),
          baseUrl,
          '/azure/list-backup-applications',
          currentProjectId,
          accountType,
          tenantId
        );
      },
    },
    managementGroups: {
      useList: () =>
        httpClient.execute(
          accountsApi.listAzureManagementGroups.bind(accountsApi),
          baseUrl,
          '/management-groups',
          currentProjectId
        ),
      activate: (managementGroupId: string) =>
        accountsApi.activateAzureManagementGroup(
          currentProjectId,
          managementGroupId
        ),
      deactivate: (managementGroupId: string) =>
        accountsApi.deactivateAzureManagementGroup(
          currentProjectId,
          managementGroupId
        ),
    },
    awsOrganization: {
      useList: (projectId?: string) =>
        httpClient.execute(
          accountsApi.listAwsOrganizations.bind(accountsApi),
          baseUrl,
          '/aws-organizations',
          projectId || currentProjectId
        ),
      connect: (connectAwsOrganizationRequest: ConnectAwsOrganizationRequest) =>
        accountsApi.connectAwsOrganization(
          currentProjectId,
          connectAwsOrganizationRequest
        ),
      activate: (organizationId: string) =>
        accountsApi.activateAwsOrganization(currentProjectId, organizationId),
      deactivate: (organizationId: string) =>
        accountsApi.deactivateAwsOrganization(currentProjectId, organizationId),
    },
    gcpOrganization: {
      useList: (projectId?: string) =>
        httpClient.execute(
          accountsApi.listGcpOrganizations.bind(accountsApi),
          baseUrl,
          '/gcp-organizations',
          projectId || currentProjectId
        ),
      connect: (connectGcpOrganizationRequest: ConnectGcpOrganizationRequest) =>
        accountsApi.connectGcpOrganization(
          currentProjectId,
          connectGcpOrganizationRequest
        ),
      activate: (organizationId: string) =>
        accountsApi.activateGcpOrganization(currentProjectId, organizationId),
      deactivate: (organizationId: string) =>
        accountsApi.deactivateGcpOrganization(currentProjectId, organizationId),
    },
    gcpFolder: {
      useList: (projectId?: string) =>
        httpClient.execute(
          accountsApi.listGcpFolders.bind(accountsApi),
          baseUrl,
          '/gcp-folders',
          projectId || currentProjectId
        ),
      connect: (connectGcpFolderRequest: ConnectGcpFolderRequest) =>
        accountsApi.connectGcpFolder(currentProjectId, connectGcpFolderRequest),
      activate: (folderId: string) =>
        accountsApi.activateGcpFolder(currentProjectId, folderId),
      deactivate: (folderId: string) =>
        accountsApi.deactivateGcpFolder(currentProjectId, folderId),
    },
    eksClusters: {
      useList: () =>
        httpClient.execute(
          accountsApi.listEksClusters.bind(accountsApi),
          baseUrl,
          '/eks-clusters',
          currentProjectId
        ),
    },
    stack: {
      get: (accountId: string) =>
        getAWSStackDetailsApi.getAWSStackDetails(currentProjectId, accountId),
    },
  };
};
